import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useI18n } from '../context/I18nContext';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import RecipeImage from '../components/RecipeImage';
import { Loader2, ArrowLeft, ChevronDown, ChevronUp, Flame, Dumbbell, Bed, BookmarkPlus, Repeat, CalendarDays, Upload } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Which recipe categories can fill each meal. Mirrors MEAL_TYPE_TO_RECIPE_CATS in the backend:
// a dinner accepts both dishes, since a menu's dinner is a main plus a side.
const MEAL_CATEGORIES = {
  breakfast: ['breakfast'], lunch: ['lunch'],
  dinner: ['dinner_main', 'dinner_secondary'], snack: ['snack'],
};

// Strings that carry a count come as [singular, plural]. Spanish and English both need it:
// "1 menús creados" reads as broken software.
const plural = (entry, n) => (Array.isArray(entry) ? (n === 1 ? entry[0] : entry[1]) : entry).replace('{n}', n);

const L = {
  es: {
    back: 'Volver a planes nutricionales', week: 'Semana', training: 'Entreno', rest: 'Descanso',
    target: 'Objetivo', day: 'Día', saveRecipe: 'Guardar como receta', saved: 'Guardada como receta',
    weekdays: { monday: 'Lunes', tuesday: 'Martes', wednesday: 'Miércoles', thursday: 'Jueves', friday: 'Viernes', saturday: 'Sábado', sunday: 'Domingo' },
    meals: { breakfast: 'Desayuno', lunch: 'Comida', dinner: 'Cena', snack: 'Snack' },
    change: 'Cambiar receta', changeTitle: 'Elegir receta para {meal}',
    search: 'Buscar receta...', changed: 'Receta cambiada', noRecipes: 'No tienes recetas de este tipo. Créalas en la sección de Recetas.',
    noMatch: 'Ninguna receta coincide con la búsqueda.', cancel: 'Cancelar',
    importMenu: 'Traer del menú', importAll: 'Traer todo del menú',
    importing: 'Importando...',
    importNothing: 'No se copió nada.',
    missingWeeks: 'Sin menú para la semana del {weeks}',
    unfilled: 'El menú no tiene {meals}, así que esas comidas se han quedado como estaban.',
    extraSnacks: 'Tu plan tiene más de un snack al día y el menú solo guarda uno: se ha rellenado el primero.',
    goMenus: 'Ir a Menús',
    exportMenu: 'Crear menú', exportAll: 'Crear menús de todo el plan',
    exporting: 'Creando menú...',
    exported: ['1 menú creado', '{n} menús creados'],
    exportedOver: ['1 menú sobrescrito', '{n} menús sobrescritos'],
    recipesCreated: ['1 receta nueva añadida a tu biblioteca: {names}', '{n} recetas nuevas añadidas a tu biblioteca: {names}'],
    importedMeals: ['1 comida copiada del menú', '{n} comidas copiadas del menú'],
    inDays: [' en 1 día', ' en {n} días'],
    exportSkipped: 'Algunas comidas se quedaron fuera por no tener nombre o no poder crearse: {meals}',
    overwriteTitle: '¿Sobrescribir los menús que ya tienes?',
    overwriteBody: 'Ya existe un menú para {weeks}. Si sigues, se reemplazarán las comidas de los días que cubre este plan. Los días que el plan no toca se quedan como están.',
    overwriteYes: 'Sí, sobrescribir', overwriteNo: 'Cancelar',
    exportSlow: 'Los platos de IA que no tengas en tu biblioteca se crearán con receta e imagen. Puede tardar.',
  },
  en: {
    back: 'Back to nutrition plans', week: 'Week', training: 'Training', rest: 'Rest',
    target: 'Target', day: 'Day', saveRecipe: 'Save as recipe', saved: 'Saved as recipe',
    weekdays: { monday: 'Monday', tuesday: 'Tuesday', wednesday: 'Wednesday', thursday: 'Thursday', friday: 'Friday', saturday: 'Saturday', sunday: 'Sunday' },
    meals: { breakfast: 'Breakfast', lunch: 'Lunch', dinner: 'Dinner', snack: 'Snack' },
    change: 'Change recipe', changeTitle: 'Pick a recipe for {meal}',
    search: 'Search recipe...', changed: 'Recipe changed', noRecipes: 'You have no recipes of this type. Create some in the Recipes section.',
    noMatch: 'No recipe matches your search.', cancel: 'Cancel',
    importMenu: 'Bring from menu', importAll: 'Bring everything from menu',
    importing: 'Importing...',
    importNothing: 'Nothing was copied.',
    missingWeeks: 'No menu for the week of {weeks}',
    unfilled: 'Your menu has no {meals}, so those meals were left as they were.',
    extraSnacks: 'Your plan has more than one snack a day and a menu only stores one: the first was filled.',
    goMenus: 'Go to Menus',
    exportMenu: 'Create menu', exportAll: 'Create menus for the whole plan',
    exporting: 'Creating menu...',
    exported: ['1 menu created', '{n} menus created'],
    exportedOver: ['1 menu overwritten', '{n} menus overwritten'],
    recipesCreated: ['1 new recipe added to your library: {names}', '{n} new recipes added to your library: {names}'],
    importedMeals: ['1 meal copied from your menu', '{n} meals copied from your menu'],
    inDays: [' across 1 day', ' across {n} days'],
    exportSkipped: 'Some meals were left out because they had no name or could not be created: {meals}',
    overwriteTitle: 'Overwrite the menus you already have?',
    overwriteBody: 'A menu already exists for {weeks}. If you continue, the meals of the days this plan covers will be replaced. Days the plan does not touch stay as they are.',
    overwriteYes: 'Yes, overwrite', overwriteNo: 'Cancel',
    exportSlow: 'AI dishes not already in your library will be created with a recipe and image. This can take a while.',
  },
};

export default function NutritionPlanDetailPage() {
  const { language } = useI18n();
  const navigate = useNavigate();
  const { planId } = useParams();
  const t = L[language] || L.es;

  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openWeek, setOpenWeek] = useState(0);
  const [savingMeal, setSavingMeal] = useState(null);
  const [recipes, setRecipes] = useState([]);
  const [picker, setPicker] = useState(null);   // {wi, di, mi, mealType} | null
  const [query, setQuery] = useState('');
  const [swapping, setSwapping] = useState(null);
  const [importing, setImporting] = useState(null);   // week index | 'all' | null
  const [exporting, setExporting] = useState(null);
  const [confirm, setConfirm] = useState(null);       // {weekIndex, weeks[]} | null

  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/nutrition/plans/${planId}`, { credentials: 'include' });
        if (res.ok) setPlan(await res.json());
        else { toast.error('Error'); navigate('/nutrition/plans'); }
      } catch { navigate('/nutrition/plans'); }
      finally { setLoading(false); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [planId]);

  // Loaded once, up front: the picker has to open instantly when a meal is tapped.
  useEffect(() => {
    (async () => {
      try {
        const res = await fetch(`${API}/recipes`, { credentials: 'include' });
        if (res.ok) {
          const d = await res.json();
          setRecipes(Array.isArray(d) ? d : (d.recipes || []));
        }
      } catch { /* the picker will just say there are no recipes */ }
    })();
  }, []);

  const pickerRecipes = useMemo(() => {
    if (!picker) return [];
    const cats = MEAL_CATEGORIES[picker.mealType] || [picker.mealType];
    const q = query.trim().toLowerCase();
    return recipes
      .filter(r => cats.some(c => (r.categories || []).includes(c) || r.category === c))
      .filter(r => !q || (r.name || '').toLowerCase().includes(q));
  }, [picker, recipes, query]);

  const importMenu = async (weekIndex) => {
    setImporting(weekIndex === null ? 'all' : weekIndex);
    try {
      const res = await fetch(`${API}/nutrition/plans/${planId}/import-menu`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ week_index: weekIndex }),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) {
        // The "no menu exists" case is the expected one, not a crash: say it plainly and
        // give them the way out instead of a bare red toast.
        toast.error(body.detail || t.importNothing, {
          action: { label: t.goMenus, onClick: () => navigate('/menus') },
          duration: 8000,
        });
        return;
      }
      // Refetch: an import rewrites whole days, so patching by hand would drift from the server.
      const fresh = await fetch(`${API}/nutrition/plans/${planId}`, { credentials: 'include' });
      if (fresh.ok) setPlan(await fresh.json());

      toast.success(plural(t.importedMeals, body.meals_filled) + plural(t.inDays, body.days_filled));
      if (body.missing_weeks?.length) {
        toast.warning(t.missingWeeks.replace('{weeks}', body.missing_weeks.join(', ')), {
          action: { label: t.goMenus, onClick: () => navigate('/menus') }, duration: 8000,
        });
      }
      if (body.unfilled_meals?.length) {
        toast.message(t.unfilled.replace('{meals}', body.unfilled_meals.map(m => (t.meals[m] || m).toLowerCase()).join(', ')));
      }
      if (body.extra_snacks) toast.message(t.extraSnacks);
    } catch { toast.error('Error'); }
    finally { setImporting(null); }
  };

  // Two-step on purpose: the first call reports a conflict instead of destroying anything,
  // and only a confirmed second call passes overwrite.
  const exportMenu = async (weekIndex, overwrite = false) => {
    setExporting(weekIndex === null ? 'all' : weekIndex);
    try {
      const res = await fetch(`${API}/nutrition/plans/${planId}/export-menu`, {
        method: 'POST', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ week_index: weekIndex, overwrite }),
      });
      const body = await res.json().catch(() => ({}));

      if (res.status === 409 && body.conflict) {
        setConfirm({ weekIndex, weeks: body.existing_weeks || [] });
        return;
      }
      if (!res.ok) { toast.error(body.detail || 'Error'); return; }

      if (body.menus_created) toast.success(plural(t.exported, body.menus_created), {
        action: { label: t.goMenus, onClick: () => navigate('/menus') }, duration: 8000,
      });
      if (body.menus_overwritten) toast.success(plural(t.exportedOver, body.menus_overwritten), {
        action: { label: t.goMenus, onClick: () => navigate('/menus') }, duration: 8000,
      });
      if (body.recipes_created?.length) {
        const names = body.recipes_created.slice(0, 3).join(', ') + (body.recipes_created.length > 3 ? '...' : '');
        toast.message(plural(t.recipesCreated, body.recipes_created.length).replace('{names}', names), { duration: 8000 });
      }
      if (body.skipped_meals?.length) {
        toast.warning(t.exportSkipped.replace('{meals}', body.skipped_meals.map(m => (t.meals[m] || m).toLowerCase()).join(', ')));
      }
      setConfirm(null);
    } catch { toast.error('Error'); }
    finally { setExporting(null); }
  };

  const swapRecipe = async (recipeId) => {
    if (!picker) return;
    setSwapping(recipeId);
    try {
      const res = await fetch(`${API}/nutrition/plans/${planId}/meal`, {
        method: 'PUT', credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          week_index: picker.wi, day_index: picker.di, meal_index: picker.mi, recipe_id: recipeId,
        }),
      });
      if (!res.ok) { const e = await res.json().catch(() => ({})); toast.error(e.detail || 'Error'); return; }
      const { meal, totals } = await res.json();
      // Patch in place from the server's answer rather than refetching the whole plan, so the
      // open week and scroll position survive the swap.
      setPlan(prev => {
        const next = structuredClone(prev);
        const day = next.week_list[picker.wi].days[picker.di];
        day.meals[picker.mi] = meal;
        day.totals = totals;
        return next;
      });
      toast.success(t.changed);
      setPicker(null);
      setQuery('');
    } catch { toast.error('Error'); }
    finally { setSwapping(null); }
  };

  const saveAsRecipe = async (wi, di, mi) => {
    const key = `${wi}-${di}-${mi}`;
    setSavingMeal(key);
    try {
      const res = await fetch(`${API}/nutrition/plans/${planId}/save-as-recipe`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, credentials: 'include',
        body: JSON.stringify({ week_index: wi, day_index: di, meal_index: mi }),
      });
      if (res.ok) toast.success(t.saved);
      else { const e = await res.json(); toast.error(e.detail || 'Error'); }
    } catch { toast.error('Error'); }
    finally { setSavingMeal(null); }
  };

  if (loading) return <Layout><div className="max-w-4xl mx-auto px-4 py-12 flex justify-center"><Loader2 className="w-8 h-8 animate-spin text-primary" /></div></Layout>;
  if (!plan) return null;

  // One in-flight write at a time: import and export both rewrite whole days, so letting
  // them overlap would race for the same document.
  const busy = importing !== null || exporting !== null;

  const macroLine = (o) => o ? `${o.calories || 0} kcal · P${o.protein_g || 0} C${o.carbs_g || 0} G${o.fat_g || 0}` : '';

  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-4 py-8">
        <button onClick={() => navigate('/nutrition/plans')} className="flex items-center gap-2 text-stone hover:text-primary mb-4 font-inter">
          <ArrowLeft className="w-4 h-4" /> {t.back}
        </button>
        <h1 className="font-manrope font-bold text-3xl text-charcoal mb-1">{plan.name}</h1>
        {plan.training_plan_name && <p className="text-sm text-stone font-inter mb-1">🏋️ {plan.training_plan_name}</p>}

        <div className="flex justify-end gap-2 flex-wrap mb-1">
          <Button
            variant="outline" onClick={() => importMenu(null)} disabled={busy}
            className="h-11 rounded-full border-primary text-primary hover:bg-primary/5"
            data-testid="import-all"
          >
            {importing === 'all'
              ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{t.importing}</>
              : <><CalendarDays className="w-4 h-4 mr-2" />{t.importAll}</>}
          </Button>
          <Button
            variant="outline" onClick={() => exportMenu(null)} disabled={busy}
            className="h-11 rounded-full border-sage text-sage hover:bg-sage/5"
            data-testid="export-all"
          >
            {exporting === 'all'
              ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" />{t.exporting}</>
              : <><Upload className="w-4 h-4 mr-2" />{t.exportAll}</>}
          </Button>
        </div>
        {/* Left-aligned on purpose: right-aligning it stranded the last word alone on its
            own line, hanging off the right edge. */}
        <p className="text-xs text-stone font-inter mb-4">{t.exportSlow}</p>

        <div className="space-y-3">
          {plan.week_list.map((wk, wi) => {
            const open = openWeek === wi;
            return (
              <Card key={wi} className="bg-white rounded-2xl shadow-soft overflow-hidden">
                <div className="w-full flex items-center justify-between p-4 gap-2">
                  <button onClick={() => setOpenWeek(open ? -1 : wi)} className="flex-1 flex items-center justify-between text-left min-w-0">
                    <span className="font-manrope font-bold text-charcoal">{t.week} {wk.week_index + 1}</span>
                  </button>
                  <Button
                    variant="ghost" size="sm" disabled={busy}
                    // stopPropagation is not enough on its own: this button used to live inside
                    // the header button, so clicking it would also toggle the week open/closed.
                    onClick={(e) => { e.stopPropagation(); importMenu(wi); }}
                    className="h-11 rounded-full text-primary shrink-0"
                    data-testid={`import-week-${wi}`}
                  >
                    {importing === wi
                      ? <Loader2 className="w-4 h-4 animate-spin" />
                      : <><CalendarDays className="w-4 h-4 mr-1.5" /><span className="hidden sm:inline">{t.importMenu}</span></>}
                  </Button>
                  <Button
                    variant="ghost" size="sm" disabled={busy}
                    onClick={(e) => { e.stopPropagation(); exportMenu(wi); }}
                    className="h-11 rounded-full text-sage shrink-0"
                    data-testid={`export-week-${wi}`}
                  >
                    {exporting === wi
                      ? <Loader2 className="w-4 h-4 animate-spin" />
                      : <><Upload className="w-4 h-4 mr-1.5" /><span className="hidden sm:inline">{t.exportMenu}</span></>}
                  </Button>
                  <button onClick={() => setOpenWeek(open ? -1 : wi)} className="shrink-0 h-11 w-8 flex items-center justify-center" aria-label={`${t.week} ${wk.week_index + 1}`}>
                    {open ? <ChevronUp className="w-5 h-5 text-stone" /> : <ChevronDown className="w-5 h-5 text-stone" />}
                  </button>
                </div>

                {open && (
                  <div className="px-4 pb-4 space-y-3">
                    {wk.days.map((day, di) => (
                      <div key={di} className="border border-border rounded-2xl p-4">
                        <div className="flex items-center justify-between mb-2">
                          <p className="font-manrope font-semibold text-charcoal">
                            {t.weekdays[day.weekday] || day.weekday} <span className="text-xs text-stone font-normal">{day.date}</span>
                          </p>
                          <span className={`flex items-center gap-1 px-3 py-1 rounded-full text-xs ${day.is_training_day ? 'bg-primary/10 text-primary' : 'bg-sage/10 text-sage'}`}>
                            {day.is_training_day ? <Dumbbell className="w-3.5 h-3.5" /> : <Bed className="w-3.5 h-3.5" />}
                            {day.is_training_day ? t.training : t.rest}
                          </span>
                        </div>

                        {day.meals.map((m, mi) => (
                          <div key={mi} className="flex items-start justify-between gap-2 border-t border-border/60 py-2">
                            <div className="min-w-0">
                              <p className="text-xs text-primary font-semibold uppercase">{t.meals[m.meal_type] || m.meal_type}</p>
                              <p className="text-sm text-charcoal font-inter">{m.name || <span className="text-stone/60 italic">—</span>}</p>
                              {(m.calories != null) && <p className="text-xs text-stone">{macroLine(m)}</p>}
                            </div>
                            {/* "Save as recipe" goes FIRST and only exists for AI meals. Putting
                                it last made the change button sit at a different x depending on
                                whether it was there, so the column never lined up. Now the change
                                button is always the last child: same x on every row. */}
                            <div className="flex items-center gap-1 shrink-0">
                              {m.source === 'ai' && m.name && (
                                <Button variant="ghost" size="sm" onClick={() => saveAsRecipe(wi, di, mi)} disabled={savingMeal === `${wi}-${di}-${mi}`} className="h-11 w-11 rounded-full text-primary" title={t.saveRecipe} aria-label={t.saveRecipe}>
                                  {savingMeal === `${wi}-${di}-${mi}` ? <Loader2 className="w-4 h-4 animate-spin" /> : <BookmarkPlus className="w-4 h-4" />}
                                </Button>
                              )}
                              <Button
                                variant="ghost" size="sm" title={t.change}
                                aria-label={`${t.change}: ${t.meals[m.meal_type] || m.meal_type}`}
                                onClick={() => { setQuery(''); setPicker({ wi, di, mi, mealType: m.meal_type }); }}
                                className="h-11 w-11 rounded-full text-stone hover:text-primary"
                                data-testid={`change-${wi}-${di}-${mi}`}
                              >
                                <Repeat className="w-4 h-4" />
                              </Button>
                            </div>
                          </div>
                        ))}

                        {/* Day totals vs target */}
                        <div className="mt-3 pt-2 border-t border-border flex items-center gap-2 flex-wrap">
                          <Flame className="w-4 h-4 text-sage" />
                          <span className="text-sm font-semibold text-charcoal">{macroLine(day.totals)}</span>
                          <span className="text-xs text-stone">/ {t.target}: {macroLine(day.target)}</span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </Card>
            );
          })}
        </div>

        <Dialog open={!!confirm} onOpenChange={(o) => { if (!o) setConfirm(null); }}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="font-manrope">{t.overwriteTitle}</DialogTitle>
              <DialogDescription className="font-inter text-stone">
                {t.overwriteBody.replace('{weeks}', (confirm?.weeks || []).join(', '))}
              </DialogDescription>
            </DialogHeader>
            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => setConfirm(null)} className="h-11 rounded-full" data-testid="overwrite-no">
                {t.overwriteNo}
              </Button>
              <Button
                onClick={() => exportMenu(confirm.weekIndex, true)} disabled={busy}
                className="h-11 rounded-full bg-primary" data-testid="overwrite-yes"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : t.overwriteYes}
              </Button>
            </div>
          </DialogContent>
        </Dialog>

        <Dialog open={!!picker} onOpenChange={(o) => { if (!o) { setPicker(null); setQuery(''); } }}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle className="font-manrope">
                {t.changeTitle.replace('{meal}', (t.meals[picker?.mealType] || picker?.mealType || '').toLowerCase())}
              </DialogTitle>
              <DialogDescription className="sr-only">{t.change}</DialogDescription>
            </DialogHeader>

            <Input
              value={query} onChange={(e) => setQuery(e.target.value)} placeholder={t.search}
              className="h-11 rounded-xl" data-testid="recipe-search"
            />

            <div className="max-h-80 overflow-y-auto space-y-2 -mx-1 px-1">
              {pickerRecipes.length === 0 ? (
                <p className="text-sm text-stone font-inter italic py-8 text-center">
                  {query.trim() ? t.noMatch : t.noRecipes}
                </p>
              ) : pickerRecipes.map(r => (
                <button
                  key={r.recipe_id} onClick={() => swapRecipe(r.recipe_id)} disabled={!!swapping}
                  className="w-full flex items-center gap-3 p-2 rounded-2xl border border-border hover:border-primary/50 hover:bg-primary/5 transition-colors text-left disabled:opacity-60"
                  data-testid={`pick-${r.recipe_id}`}
                >
                  <RecipeImage src={r.image} alt={r.name} containerClassName="w-12 h-12 rounded-xl flex-shrink-0" />
                  <div className="min-w-0 flex-1">
                    <p className="font-inter text-sm text-charcoal truncate">{r.name}</p>
                    {r.calories != null && (
                      <p className="text-xs text-stone">{macroLine(r)}</p>
                    )}
                  </div>
                  {swapping === r.recipe_id && <Loader2 className="w-4 h-4 animate-spin text-primary shrink-0" />}
                </button>
              ))}
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
}
